import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { AlertCircle, XCircle, Info, CheckCircle } from 'lucide-react';

type ConfirmVariant = 'default' | 'destructive' | 'warning' | 'info' | 'success';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  variant?: ConfirmVariant;
}

const variantStyles = {
  default: {
    container: 'border-2 border-gray-300 bg-gray-50',
    text: 'text-gray-900',
    icon: Info,
    iconColor: 'text-gray-500',
    button: 'bg-gray-600 text-white hover:bg-gray-700',
  },
  destructive: {
    container: 'border-2 border-red-500 bg-red-50',
    text: 'text-red-900',
    icon: XCircle,
    iconColor: 'text-red-500',
    button: 'bg-red-600 text-white hover:bg-red-700',
  },
  warning: {
    container: 'border-2 border-yellow-500 bg-yellow-50',
    text: 'text-yellow-900',
    icon: AlertCircle,
    iconColor: 'text-yellow-500',
    button: 'bg-yellow-600 text-white hover:bg-yellow-700',
  },
  info: {
    container: 'border-2 border-blue-500 bg-blue-50',
    text: 'text-blue-900',
    icon: Info,
    iconColor: 'text-blue-500',
    button: 'bg-blue-600 text-white hover:bg-blue-700',
  },
  success: {
    container: 'border-2 border-green-500 bg-green-50',
    text: 'text-green-900',
    icon: CheckCircle,
    iconColor: 'text-green-500',
    button: 'bg-green-600 text-white hover:bg-green-700',
  },
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  variant = 'default'
}: ConfirmDialogProps) {
  const styles = variantStyles[variant];
  const Icon = styles.icon;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={`rounded-lg shadow-lg ${styles.container}`}>
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${styles.iconColor}`} />
            <div className="flex-1">
              <AlertDialogTitle className={`text-base font-semibold ${styles.text}`}>
                {title}
              </AlertDialogTitle>
              <AlertDialogDescription className={`text-sm mt-2 ${styles.text} opacity-90`}>
                {description}
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-4">
          <AlertDialogCancel className="text-sm border-gray-300 hover:bg-gray-100">
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={`text-sm font-semibold ${styles.button}`}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Hook for using confirm dialog
export function useConfirmDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<{
    title: string;
    description: string;
    confirmText: string;
    cancelText: string;
    variant: ConfirmVariant;
    onConfirm: () => void;
  }>({
    title: '',
    description: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'default',
    onConfirm: () => {}
  });

  const showConfirm = (options: {
    title: string;
    description: string;
    confirmText?: string;
    cancelText?: string;
    variant?: ConfirmVariant;
    onConfirm: () => void;
  }) => {
    setConfig({
      title: options.title,
      description: options.description,
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      variant: options.variant || 'default',
      onConfirm: options.onConfirm
    });
    setIsOpen(true);
  };

  const Dialog = () => (
    <ConfirmDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      {...config}
    />
  );

  return { showConfirm, ConfirmDialog: Dialog };
}

// Global ConfirmDialogProvider for system-wide usage
export function GlobalConfirmDialogProvider() {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<{
    title: string;
    description: string;
    confirmText: string;
    cancelText: string;
    variant: ConfirmVariant;
  }>({
    title: '',
    description: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'default',
  });

  useEffect(() => {
    // Import dynamically to avoid circular dependency
    import('../../src/utils/notification.utils').then((utils) => {
      utils.registerConfirmDialogListener((dialogConfig) => {
        setConfig({
          title: dialogConfig.title,
          description: dialogConfig.description,
          confirmText: dialogConfig.confirmText,
          cancelText: dialogConfig.cancelText,
          variant: dialogConfig.variant,
        });
        setIsOpen(dialogConfig.isOpen);
      });
    });
  }, []);

  const handleConfirm = () => {
    import('../../src/utils/notification.utils').then((utils) => {
      utils.handleConfirmResponse(true);
    });
    setIsOpen(false);
  };

  const handleCancel = () => {
    import('../../src/utils/notification.utils').then((utils) => {
      utils.handleConfirmResponse(false);
    });
    setIsOpen(false);
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <AlertDialogContent className={`rounded-lg shadow-lg ${variantStyles[config.variant].container}`}>
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            {(() => {
              const Icon = variantStyles[config.variant].icon;
              return <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${variantStyles[config.variant].iconColor}`} />;
            })()}
            <div className="flex-1">
              <AlertDialogTitle className={`text-base font-semibold ${variantStyles[config.variant].text}`}>
                {config.title}
              </AlertDialogTitle>
              <AlertDialogDescription className={`text-sm mt-2 ${variantStyles[config.variant].text} opacity-90`}>
                {config.description}
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-4">
          <AlertDialogCancel
            className="text-sm border-gray-300 hover:bg-gray-100"
            onClick={handleCancel}
          >
            {config.cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className={`text-sm font-semibold ${variantStyles[config.variant].button}`}
          >
            {config.confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
